import { useMemo, useState } from 'react';
import { useLocation } from 'wouter';

import { Header } from '~/client/components/Header';
import { Icon } from '~/client/components/Icons';
import { Button, Chip, Field } from '~/client/components/Primitives';
import { ApiError, createPoll } from '~/client/lib/api';
import { rememberPoll } from '~/client/lib/myPolls';
import { saveOwnerToken } from '~/client/lib/tokens';
import { formatRangeLabel, isValidIsoDay, rangeLength } from '~/shared/dates';
import { browserTimeZone, commonTimeZones, isValidTimeZone, nowIsoDay } from '~/shared/tz';

const EXPIRY_OPTIONS = [
  { value: 'never', label: 'never expires' },
  { value: '24h', label: 'in 24 hours', ms: 24 * 60 * 60 * 1000 },
  { value: '7d', label: 'in 7 days', ms: 7 * 24 * 60 * 60 * 1000 },
  { value: '30d', label: 'in 30 days', ms: 30 * 24 * 60 * 60 * 1000 },
] as const;

type ExpiryValue = (typeof EXPIRY_OPTIONS)[number]['value'];

const MAX_WINDOW = 366;

export function Create() {
  const [, navigate] = useLocation();
  const browserTz = useMemo(() => browserTimeZone(), []);
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [timeZone, setTimeZone] = useState(browserTz);
  const [expiry, setExpiry] = useState<ExpiryValue>('30d');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const today = useMemo(() => nowIsoDay(timeZone), [timeZone]);

  const validRange = isValidIsoDay(startDate) && isValidIsoDay(endDate) && startDate <= endDate;
  const days = validRange ? rangeLength(startDate, endDate) : 0;
  const tooManyDays = days > MAX_WINDOW;
  const canSubmit =
    !submitting &&
    title.trim().length > 0 &&
    validRange &&
    !tooManyDays &&
    isValidTimeZone(timeZone);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    const opt = EXPIRY_OPTIONS.find((o) => o.value === expiry);
    const expiresAt = opt && 'ms' in opt ? Date.now() + opt.ms : null;
    try {
      const result = await createPoll({
        title: title.trim(),
        startDate,
        endDate,
        timeZone,
        expiresAt,
      });
      saveOwnerToken(result.id, result.ownerToken);
      rememberPoll({ id: result.id, title: title.trim(), role: 'owner' });
      navigate(`/p/${result.id}`);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'something went wrong';
      setError(message);
      setSubmitting(false);
    }
  };

  const tzOptions = useMemo(() => {
    const set = new Set<string>([browserTz, ...commonTimeZones()]);
    return Array.from(set).filter((tz) => isValidTimeZone(tz));
  }, [browserTz]);

  return (
    <>
      <Header />
      <main className="app-main">
        <form className="container container-narrow" onSubmit={handleSubmit}>
          <Button
            variant="text"
            onClick={() => navigate('/')}
            style={{ paddingLeft: 0, marginBottom: 16 }}
          >
            <Icon name="arrow-l" size={14} /> back
          </Button>
          <h1 className="page-title">new poll</h1>
          <p className="muted" style={{ marginTop: 0, marginBottom: 24 }}>
            give it a name and pick the window of dates everyone might be free.
          </p>

          <div className="col" style={{ gap: 20 }}>
            <Field label="poll title" htmlFor="title">
              <input
                id="title"
                className="input"
                placeholder="e.g. lads holiday — august"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={80}
                autoComplete="off"
              />
            </Field>

            <div className="row row-wrap" style={{ gap: 16 }}>
              <div style={{ flex: '1 1 200px' }}>
                <Field label="from" htmlFor="start">
                  <input
                    id="start"
                    type="date"
                    className="input"
                    value={startDate}
                    min={today}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </Field>
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <Field label="to" htmlFor="end">
                  <input
                    id="end"
                    type="date"
                    className="input"
                    value={endDate}
                    min={startDate || today}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </Field>
              </div>
            </div>

            {validRange && (
              <div className="row" style={{ gap: 8 }}>
                <Chip variant="soft" dot>
                  {formatRangeLabel(startDate, endDate)} · {days} day{days === 1 ? '' : 's'}
                </Chip>
              </div>
            )}
            {tooManyDays && (
              <div
                role="alert"
                style={{
                  padding: '10px 14px',
                  background: 'var(--coral-soft)',
                  color: 'var(--coral-deep)',
                  borderRadius: 10,
                  fontSize: 14,
                }}
              >
                that's a lot of days — keep it under 366 for now.
              </div>
            )}

            <div className="row row-wrap" style={{ gap: 16 }}>
              <div style={{ flex: '1 1 240px' }}>
                <Field label="time zone" htmlFor="tz">
                  <select
                    id="tz"
                    className="input"
                    value={timeZone}
                    onChange={(e) => setTimeZone(e.target.value)}
                  >
                    {tzOptions.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <Field label="link expires" htmlFor="expiry">
                  <select
                    id="expiry"
                    className="input"
                    value={expiry}
                    onChange={(e) => setExpiry(e.target.value as ExpiryValue)}
                  >
                    {EXPIRY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </div>

            {error && (
              <div
                role="alert"
                style={{
                  padding: '10px 14px',
                  background: 'var(--coral-soft)',
                  color: 'var(--coral-deep)',
                  borderRadius: 10,
                  fontSize: 14,
                }}
              >
                {error}
              </div>
            )}

            <div className="sticky-actions">
              <Button variant="ghost" onClick={() => navigate('/')}>
                cancel
              </Button>
              <Button variant="primary" size="lg" type="submit" disabled={!canSubmit}>
                {submitting ? 'making the link…' : 'make the link'}
                {!submitting && <Icon name="arrow" size={16} />}
              </Button>
            </div>
          </div>
        </form>
      </main>
    </>
  );
}
